/**
 * Access Control Utility
 * Manages department-based access control in the frontend
 */

// Access levels
export const FULL_ACCESS = 'full';
export const VIEW_ONLY = 'view';
export const NO_ACCESS = 'none';

// Module to sidebar route mapping
export const MODULE_ROUTES = {
  dashboard: ['/', '/dashboard/employee', '/dashboard/management'],
  command_centre: ['/command-centre'],
  projects: ['/projects'],
  project_schedule: ['/project-schedule'],
  drawings_view: ['/project-drawings'],
  tasks: ['/tasks'],
  documents: ['/documents'],
  dept_templates: ['/department-docs'],
  employees: ['/employees'],
  departments: ['/departments'],
  roles: ['/roles'],
  workflows: ['/workflows'],
  activity_logs: ['/activity'],
  desktop_activity: ['/desktop-activity'],
  notifications: ['/notifications'],
  messaging: ['/messaging'],
  society_members: ['/society-members'],
  task_library: ['/task-library'],
  insurance: ['/contractor-insurance'],
  compliance: ['/compliance-alerts'],
  alert_config: ['/alert-config'],
  alerts: ['/alerts'],
  ai_insights: ['/ai-insights'],
  all_reports: ['/reports'],
  code_health: ['/code-health'],
  whatsapp: ['/whatsapp'],
};

// Sidebar items with their required modules
export const SIDEBAR_ITEMS = [
  { path: '/dashboard', module: 'dashboard', label: 'Dashboard', managerOnly: false },
  { path: '/command-centre', module: 'command_centre', label: 'Daily Operations', managerOnly: true },
  { path: '/projects', module: 'projects', label: 'Projects', managerOnly: false },
  { path: '/project-schedule', module: 'project_schedule', label: 'Project Schedule', managerOnly: false },
  { path: '/project-drawings', module: 'drawings_view', label: 'Drawings', managerOnly: false },
  { path: '/tasks', module: 'tasks', label: 'My Tasks', managerOnly: false },
  { path: '/documents', module: 'documents', label: 'Documents', managerOnly: false },
  { path: '/department-docs', module: 'dept_templates', label: 'Dept. Templates', managerOnly: false },
  { path: '/employees', module: 'employees', label: 'Employees', managerOnly: true },
  { path: '/departments', module: 'departments', label: 'Departments', managerOnly: true },
  { path: '/roles', module: 'roles', label: 'Roles', managerOnly: true },
  { path: '/workflows', module: 'workflows', label: 'Workflows', managerOnly: true },
  { path: '/activity', module: 'activity_logs', label: 'Activity', managerOnly: false },
  { path: '/desktop-activity', module: 'desktop_activity', label: 'Desktop Tracking', managerOnly: true },
  { path: '/notifications', module: 'notifications', label: 'Notifications', managerOnly: false },
  { path: '/messaging', module: 'messaging', label: 'Messages', managerOnly: false },
  { path: '/society-members', module: 'society_members', label: 'Society Members', managerOnly: false },
  { path: '/task-library', module: 'task_library', label: 'Task Library', managerOnly: false },
  { path: '/contractor-insurance', module: 'insurance', label: 'Contractor Insurance', managerOnly: false },
  { path: '/compliance-alerts', module: 'compliance', label: 'Compliance Alerts', managerOnly: false },
  { path: '/alert-config', module: 'alert_config', label: 'Alert Configuration', adminOnly: true },
  { path: '/alerts', module: 'alerts', label: 'Alerts', managerOnly: false },
  { path: '/ai-insights', module: 'ai_insights', label: 'AI Insights', managerOnly: true },
  { path: '/reports', module: 'all_reports', label: 'Reports', managerOnly: true },
  { path: '/code-health', module: 'code_health', label: 'Code Health', adminOnly: true },
  { path: '/whatsapp', module: 'whatsapp', label: 'WhatsApp', managerOnly: false },
];

/**
 * Check if a user has access to a specific module
 * @param {Object} accessProfile - User's access profile from API
 * @param {string} module - Module name to check
 * @param {string} requiredLevel - Required access level (default: 'view')
 * @returns {boolean}
 */
export function hasModuleAccess(accessProfile, module, requiredLevel = VIEW_ONLY) {
  if (!accessProfile || !accessProfile.modules) return false;

  // Admin has full access
  if (accessProfile.can_access_all || accessProfile.is_admin) return true;

  const moduleAccess = accessProfile.modules[module];
  if (!moduleAccess || moduleAccess === NO_ACCESS) return false;

  if (requiredLevel === FULL_ACCESS) {
    return moduleAccess === FULL_ACCESS;
  }

  // VIEW_ONLY accepts both 'full' and 'view'
  return moduleAccess === FULL_ACCESS || moduleAccess === VIEW_ONLY;
}

/**
 * Check if user can access a route
 * @param {Object} accessProfile - User's access profile
 * @param {string} path - Route path to check
 * @returns {boolean}
 */
export function canAccessRoute(accessProfile, path) {
  if (!accessProfile) return false;
  if (accessProfile.can_access_all || accessProfile.is_admin) return true;

  // Find which module this route belongs to
  for (const [module, routes] of Object.entries(MODULE_ROUTES)) {
    if (routes.some(route => path.startsWith(route))) {
      return hasModuleAccess(accessProfile, module);
    }
  }

  // Default allow for unmatched routes
  return true;
}

/**
 * Filter sidebar items based on access profile
 * @param {Object} accessProfile - User's access profile
 * @param {Object} user - User object with role info
 * @returns {Array} Filtered sidebar items
 */
export function getAccessibleSidebarItems(accessProfile, user) {
  if (!accessProfile || !user) return [];

  const role = (user.role_name || user.role || '').toLowerCase();
  const isAdmin = role === 'admin';
  const isManager = isAdmin || role === 'manager';

  return SIDEBAR_ITEMS.filter(item => {
    // Check admin-only items
    if (item.adminOnly && !isAdmin) return false;

    // Check manager-only items (for role-based restrictions)
    if (item.managerOnly && !isManager) return false;

    // Check department-based access
    return hasModuleAccess(accessProfile, item.module);
  });
}

/**
 * Check if user should see edit/delete buttons (full access)
 * @param {Object} accessProfile - User's access profile
 * @param {string} module - Module name
 * @returns {boolean}
 */
export function hasFullAccess(accessProfile, module) {
  return hasModuleAccess(accessProfile, module, FULL_ACCESS);
}

export default {
  FULL_ACCESS,
  VIEW_ONLY,
  NO_ACCESS,
  hasModuleAccess,
  canAccessRoute,
  getAccessibleSidebarItems,
  hasFullAccess,
};
